/**
 * Pagina WhatsApi - Estatisticas (TASK-2259).
 *
 * Observabilidade do WhatsApi. Compoe os componentes criados em TASK-2258 dentro de
 * WhatsApiPeriodProvider.
 *
 * Escopo (useEscopoWhatsApi): com tenant, os cards usam /v1/tenants/{id}/whatsapi/stats/*
 * (so as chaves do tenant); com organizacao, /v1/organizations/{id}/whatsapi/stats/*
 * (gate `pode.observe` OU `pode.read`, sem card de saude). O painel global
 * /v1/whatsapi-proxy/stats/* (todas as chaves da plataforma, para admin/devops) so
 * aparece SEM tenant e sem organizacao: com uma conta escolhida, mostrar o agregado
 * global e exibir dado de WhatsApp de outra conta (#26090).
 */

import { Alert, Grid, Group, Skeleton, Stack } from '@mantine/core'
import { IconKey, IconLock } from '@tabler/icons-react'

import { EmptyState } from '@/components/ui'
import { ApiKeyTable } from '@/components/whatsapi/ApiKeyTable'
import { DeliveryFunnel } from '@/components/whatsapi/DeliveryFunnel'
import { ErrorsTable } from '@/components/whatsapi/ErrorsTable'
import { LatencyChart } from '@/components/whatsapi/LatencyChart'
import { PeriodSelector } from '@/components/whatsapi/PeriodSelector'
import { SeletorEscopo } from '@/components/whatsapi/SeletorEscopo'
import { TituloDoEscopo } from '@/components/whatsapi/TituloDoEscopo'
import { StatCardsRow } from '@/components/whatsapi/StatCardsRow'
import { ThroughputChart } from '@/components/whatsapi/ThroughputChart'
import { WhatsApiPeriodProvider, useWhatsApiPeriod } from '@/context/WhatsApiPeriodContext'
import { useEscopoWhatsApi, type EscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'
import { detalheDo424, isNoApiKeys, useOverview } from '@/hooks/useWhatsApiStats'
import { useCan } from '@/store/auth'

export function formatPct(part: number, total: number): string {
  if (total === 0) return '0.0'
  return ((part / total) * 100).toFixed(1)
}

export function formatHour(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Os cards do escopo; no tenant sem chave ou sem WhatsApi, o aviso no lugar deles. */
function PainelDoEscopo({ escopo }: { escopo?: Exclude<EscopoWhatsApi, { tipo: 'nenhum' }> }) {
  const { period } = useWhatsApiPeriod()
  // Mesma queryKey do StatCardsRow/DeliveryFunnel: o react-query reaproveita a resposta.
  const sonda = useOverview(period, { escopo })

  if (escopo?.tipo === 'tenant' && isNoApiKeys(sonda.data)) {
    return (
      <EmptyState
        icon={IconKey}
        accent="info"
        title="Este tenant ainda não tem API keys do WhatsApi"
        description="Configure uma chave de API para começar a monitorar throughput, latência e erros de mensageria."
        primaryAction={{ label: 'Ver API Keys', href: '/whatsapi/api-keys' }}
      />
    )
  }
  if (escopo?.tipo === 'tenant' && detalheDo424(sonda.error) === 'whatsapi_disabled') {
    return (
      <EmptyState
        icon={IconKey}
        accent="info"
        title="O WhatsApi não está habilitado neste tenant"
        description="Peça a um administrador para habilitar o WhatsApi neste tenant."
      />
    )
  }

  return (
    <>
      <StatCardsRow escopo={escopo} />

      <DeliveryFunnel escopo={escopo} />

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <ThroughputChart escopo={escopo} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <LatencyChart escopo={escopo} />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <ApiKeyTable escopo={escopo} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <ErrorsTable escopo={escopo} />
        </Grid.Col>
      </Grid>
    </>
  )
}

export function WhatsApiStats() {
  const can = useCan()
  const { escopo, opcoes, escolher, carregando, nome } = useEscopoWhatsApi()
  // Tenant e organizacao usam as proprias rotas; so sem nenhum dos dois o painel global.
  const escopoDosCards = escopo.tipo === 'nenhum' ? undefined : escopo
  // O gate de cada escopo e o das rotas que ele chama: observe OU read no tenant e na
  // organizacao; observe no painel global.
  const allowed = escopo.tipo === 'organizacao'
    ? escopo.pode.observe || escopo.pode.read
    : escopo.tipo === 'tenant'
      ? can('whatsapi', 'observe') || can('whatsapi', 'read')
      : can('whatsapi', 'observe')

  // Sem tenant e com a lista de organizacoes a caminho: esperar, em vez de pedir o
  // painel global (que responde 424 para quem nao tem tenant).
  if (carregando) {
    return <Skeleton height={120} radius="sm" />
  }

  if (!allowed) {
    return (
      <Alert icon={<IconLock size={16} />} color="yellow" variant="light">
        Você não tem permissão para visualizar a observabilidade do WhatsApi.
      </Alert>
    )
  }

  return (
    <WhatsApiPeriodProvider>
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="md" align="center">
            <TituloDoEscopo titulo="Observabilidade WhatsApi" escopo={escopo} nome={nome} />
            <SeletorEscopo escopo={escopo} opcoes={opcoes} escolher={escolher} />
          </Group>
          <PeriodSelector />
        </Group>

        <PainelDoEscopo escopo={escopoDosCards} />
      </Stack>
    </WhatsApiPeriodProvider>
  )
}
